// @ts-check

import { DatePicker, Input, Modal, Select, Spin } from "antd";
import Button from "~/components/Shared/Button";
import FormGroup from "~/components/Shared/Forms/FormGroup";
import { useEffect, useMemo, useState } from "react";
import { TASK_STATUS_OPTIONS } from "~/components/Tasks/tasks";
import { isEmpty } from "lodash-es";
import { tasksApi } from "~/api/zero-api";
import dayjs from "dayjs";
import NotificationAlert from "~/other/NotificationAlert";
import { getErrorMessageFromResponse, mapErrorMessages } from "~/other/Helper";
import AvatarAssignmentList from "~/components/Shared/AvatarAssignmentList";
import { useCurrentUser } from "~/hooks/reduxHooks";

const TODO_MAX_TITLE_LENGTH = 200;

function getInitialTaskFormValues(task, taskId, user) {
    if (!task && !taskId) {
        return {
            title: "",
            status: "open",
            dueDate: dayjs().add(1, "day"),
            assignedUsers: [user],
        };
    }

    return {
        title: task?.title ?? "",
        status: task?.status ?? "open",
        dueDate: task?.due_date ? dayjs(task.due_date) : null,
        assignedUsers: task?.assigned_users ?? [],
    };
}

/**
 * @param {DirectoryUser[]} users
 * @returns {DirectoryUser[]}
 */
function excludeViewersFilter(users) {
    return users.filter((user) => user.role !== "viewer");
}

/**
 * @param {{
 *  task?: Task,
 *  taskId?: string,
 *  onClose: () => void,
 *  onMutation?: (task: Task) => void,
 *  onOfflineMutation?: (task: TaskUpsertBody, taskId?: string) => void,
 *  submissionId?: string,
 *  fieldId?: string
 * }} props
 */
export default function TaskDetailModal({
    task: initialTask,
    taskId,
    onClose,
    onMutation,
    onOfflineMutation,
    submissionId = null,
    fieldId = null,
}) {
    const user = useCurrentUser();
    const [task, setTask] = useState(initialTask ?? null);
    const [loadingTask, setLoadingTask] = useState(!!taskId);
    const [loadingTaskError, setLoadingTaskError] = useState(null);
    const isCreationModal = !initialTask && !taskId;

    useEffect(() => {
        if (taskId) {
            const fetchTask = async () => {
                try {
                    const res = await tasksApi.retrieve(taskId);
                    const task = await res.json();
                    setTask(task);
                    setLoadingTask(false);
                } catch (err) {
                    setLoadingTaskError(err);
                    setLoadingTask(false);
                }
            };
            fetchTask();
        }
    }, [taskId]);

    const initialValues = useMemo(() => getInitialTaskFormValues(task, taskId, user), [task, taskId, user]);

    const [loading, setLoading] = useState(false);
    /** @type {useState<{title?: string, assignedUsers?: string, dueDate?: string, status?: string}>} */
    const [errors, setErrors] = useState({});
    const [title, setTitle] = useState(initialValues.title);
    const [status, setStatus] = useState(initialValues.status);
    const [dueDate, setDueDate] = useState(initialValues.dueDate);
    const [assignedUsers, setAssignedUsers] = useState(initialValues.assignedUsers);

    useEffect(() => {
        setTitle(initialValues.title);
        setStatus(initialValues.status);
        setDueDate(initialValues.dueDate);
        setAssignedUsers(initialValues.assignedUsers);
    }, [initialValues]);

    const unassignUser = (userToRemove) => {
        setAssignedUsers(assignedUsers.filter((user) => user.uuid !== userToRemove.uuid));
    };

    const validateInput = () => {
        const newErrors = {};

        if (!title) {
            newErrors["title"] = "Field is required.";
        } else if (title.length > TODO_MAX_TITLE_LENGTH) {
            newErrors["title"] = `Max length of ${TODO_MAX_TITLE_LENGTH} characters.`;
        }

        setErrors(newErrors);
        return isEmpty(newErrors);
    };

    const upsertTask = async () => {
        if (!validateInput()) {
            return;
        }

        try {
            /** @type {TaskUpsertBody} */
            const body = {
                title,
                status,
                due_date: dueDate?.format("YYYY-MM-DD") ?? null,
                assigned_user_uuids: assignedUsers.map((user) => user.uuid),
                submission_uuid: submissionId ?? undefined,
                submission_field_uuid: fieldId ?? undefined,
            };

            setLoading(true);

            if (submissionId?.startsWith("offline:")) {
                onOfflineMutation?.(body, task?.uuid);
            } else {
                let res;
                try {
                    const apiCall = task ? tasksApi.partialUpdate(task.uuid, body) : tasksApi.create(body);
                    res = await apiCall;
                } catch (err) {
                    if (err instanceof Response) {
                        const newErrors = await mapErrorMessages(err.clone(), {
                            title: "title",
                            status: "status",
                            assigned_user_uuids: "assignedUsers",
                            assigned_users: "assignedUsers",
                            due_date: "dueDate",
                        });
                        setErrors(newErrors);
                    }
                    throw err;
                }
                const updatedTask = await res.json();
                onMutation?.(updatedTask);
            }
            onClose();
        } catch (err) {
            NotificationAlert("error", "", `Could not ${task ? "update" : "create"} todo.`);
            const message = await getErrorMessageFromResponse(err, "Unknown error while upserting task");
            console.error(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={`${isCreationModal ? "Create" : "Edit"} To-Do`}
            open
            maskClosable={false}
            maskTransitionName="maskTransitionName"
            onCancel={onClose}
            footer={
                <div>
                    <Button type="discard" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button type="primary" onClick={upsertTask} loading={loading} disabled={loadingTask}>
                        {isCreationModal ? "Create" : "Save"}
                    </Button>
                </div>
            }
        >
            {loadingTaskError && (
                <p>Could not load to-do. Please try again later or contact support if the problem persists.</p>
            )}
            {!loadingTaskError && (
                <div>
                    <FormGroup required label="Description" error={errors.title}>
                        <Input.TextArea
                            placeholder="Enter a title..."
                            disabled={loadingTask}
                            value={title}
                            autoSize
                            maxLength={TODO_MAX_TITLE_LENGTH}
                            showCount
                            onChange={(ev) => setTitle(ev.target.value)}
                        />
                    </FormGroup>
                    <FormGroup required label="Status" error={errors.status}>
                        <Select
                            options={TASK_STATUS_OPTIONS}
                            style={{ width: "50%" }}
                            dropdownStyle={{ zIndex: "1200" }}
                            placeholder="Select a status"
                            disabled={loadingTask}
                            value={status}
                            onChange={(newStatus) => setStatus(newStatus)}
                        />
                    </FormGroup>
                    <FormGroup label="Due Date" error={errors.dueDate}>
                        <DatePicker
                            style={{ width: "50%" }}
                            disabled={loadingTask}
                            value={dueDate}
                            format={"MMM D, YYYY"}
                            onChange={setDueDate}
                        />
                    </FormGroup>
                    <FormGroup label="Assignees" error={errors.assignedUsers}>
                        <p className="zero-dark-grey mar-btm-0">Assignees will be automatically notified.</p>
                        {loadingTask ? (
                            <Spin delay={250} />
                        ) : (
                            <AvatarAssignmentList
                                assignedUsers={assignedUsers}
                                onUserChange={setAssignedUsers}
                                userFilter={excludeViewersFilter}
                                showRemoveAction
                                removeActionText="Unassign User"
                                onRemoveUser={unassignUser}
                            />
                        )}
                    </FormGroup>
                </div>
            )}
        </Modal>
    );
}
